/**
 * 天候モデル（docs/04_world-and-weather.md 9 章）：風、風下、斜面の上昇風、サーマル、積雲、雷雨。
 * 状態（WeatherState）はプレーンなオブジェクトで、計算用の格子はこのクラスが持つ。
 */
import type { DayProfile } from '../../config/weatherProfiles';
import { smoothstep } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../core/vec';
import { Biome } from '../../core/world';
import type { TerrainSampler } from '../world/terrain';
import { makeForecast, type Forecast } from './forecast';
import { sunPosition } from './sun';
import { baseWindAt, cloudBaseAt, sampleTruth, thermalRatio, type DayWeatherTruth } from './truth';
import { altitudeWindFactor } from './wind';

export interface Thermal {
  id: number;
  x: number;
  z: number;
  groundM: number;
  strengthMs: number;
  radiusM: number;
  bornS: number;
  lifeS: number;
  hasCloud: boolean;
}

export interface Cloud {
  id: number;
  thermalId: number;
  x: number;
  z: number;
  baseM: number;
  sizeM: number;
  topM: number;
  bornS: number;
  dieS: number;
  /** 雷雨の前触れで高く育つ度合い（0〜1） */
  towering: number;
}

export type StormPhase = 'waiting' | 'precursor' | 'mature' | 'dissipating' | 'done';

export interface Storm {
  id: number;
  x: number;
  z: number;
  phase: StormPhase;
  precursorS: number;
  onsetS: number;
  endS: number;
  radiusM: number;
  maxRadiusM: number;
}

export interface WeatherState {
  truth: DayWeatherTruth;
  thermals: Thermal[];
  clouds: Cloud[];
  storms: Storm[];
  nextId: number;
}

type WeatherEvent = { type: 'cuDeveloping' | 'stormFormed' | 'stormDissipated'; x: number; z: number };

const GRID_M = 500;
const HASH_M = 1000;
const MAX_THERMALS = 700;
const MEAN_LIFE_S = 2250;
const DISSIPATE_S = 30 * 60;

/** サーマルの芯の上昇に上乗せする、旋回中のグライダーの沈下 */
const THERMAL_SINK_OFFSET = 1.3;

const BIOME_THERMAL: readonly number[] = [0, 1.1, 1.0, 0.7, 0.4, 1.1, 1.2, 0.2, 1.2];

export class WeatherModel {
  state: WeatherState;
  private readonly gnx: number;
  private readonly gnz: number;
  private readonly lee: Float32Array;
  private readonly rise: Float32Array;
  private leeFromRad = NaN;
  private readonly source: Float32Array;
  private readonly sourceCdf: Float64Array;
  private sourceMax = 1;
  private sourceTime = -Infinity;
  private hash = new Map<number, Thermal[]>();
  private byId = new Map<number, Thermal>();
  private readonly stormSites: Vec2[];

  constructor(
    private readonly terrain: TerrainSampler,
    private readonly bounds: { widthM: number; depthM: number },
    stormSites: Vec2[],
  ) {
    this.gnx = Math.floor(bounds.widthM / GRID_M) + 1;
    this.gnz = Math.floor(bounds.depthM / GRID_M) + 1;
    const n = this.gnx * this.gnz;
    this.lee = new Float32Array(n);
    this.rise = new Float32Array(n);
    this.source = new Float32Array(n);
    this.sourceCdf = new Float64Array(n);
    this.stormSites = stormSites;
    this.state = {
      truth: null as unknown as DayWeatherTruth,
      thermals: [],
      clouds: [],
      storms: [],
      nextId: 1,
    };
  }

  /** 日の始めに実際の天気と予報を作る */
  startDay(profile: DayProfile, dayIndex: number, rng: Rng, timeOfDayS: number): Forecast {
    const truth = sampleTruth(profile, rng, this.stormSites);
    const storms: Storm[] = truth.storms.map((p) => ({
      id: this.state.nextId++,
      x: p.center.x,
      z: p.center.z,
      phase: 'waiting',
      precursorS: (p.onsetH - p.leadMin / 60) * 3600,
      onsetS: p.onsetH * 3600,
      endS: p.onsetH * 3600 + p.lifeMin * 60,
      radiusM: 0,
      maxRadiusM: p.radiusM,
    }));
    this.state = { truth, thermals: [], clouds: [], storms, nextId: this.state.nextId };
    this.leeFromRad = NaN;
    this.sourceTime = -Infinity;
    this.updateLee(timeOfDayS);
    return makeForecast(truth, profile, dayIndex, rng, this.stormSites);
  }

  // ---- 時間の関数 ----

  thermalRatio(timeOfDayS: number): number {
    return thermalRatio(this.state.truth.thermal, timeOfDayS);
  }

  cloudBaseAt(timeOfDayS: number): number {
    return cloudBaseAt(this.state.truth, timeOfDayS);
  }

  // ---- 空間の問い合わせ ----

  private cell(x: number, z: number): number {
    const i = Math.min(Math.max(Math.round(x / GRID_M), 0), this.gnx - 1);
    const j = Math.min(Math.max(Math.round(z / GRID_M), 0), this.gnz - 1);
    return j * this.gnx + i;
  }

  /** 風下の度合い（0〜1）。風上の尾根より高く飛んでいれば効かない */
  leeAt(x: number, y: number, z: number): number {
    const k = this.cell(x, z);
    const lee = this.lee[k] as number;
    if (lee <= 0) return 0;
    const agl = y - this.terrain.heightAt(x, z);
    return lee * Math.min(Math.max(1 - agl / Math.max(this.rise[k] as number, 1), 0), 1);
  }

  /** 水平の風（to 方向、m/s）を out に書く */
  windAt(x: number, y: number, z: number, timeOfDayS: number, out: Vec2): Vec2 {
    const w = baseWindAt(this.state.truth, timeOfDayS);
    const f = altitudeWindFactor(y) * (1 - 0.4 * this.leeAt(x, y, z));
    out.x = w.x * f;
    out.z = w.z * f;
    return out;
  }

  /** 空気の上下の流れ（m/s）：サーマル＋斜面の上昇風＋風下の下降＋背景の下降＋雷雲 */
  liftAt(x: number, y: number, z: number, timeOfDayS: number): number {
    const ground = this.terrain.heightAt(x, z);
    const agl = y - ground;
    const w = this.windAt(x, y, z, timeOfDayS, { x: 0, z: 0 });
    const windMs = Math.hypot(w.x, w.z);
    let lift = 0;

    // 斜面の上昇風（風上側の斜面）
    const g = this.terrain.gradientAt(x, z);
    const gl = Math.hypot(g.gx, g.gz);
    if (gl > 0.05 && agl < 900) {
      const up = (w.x * g.gx + w.z * g.gz) / gl;
      if (up > 0) lift += 0.9 * up * Math.sin(Math.atan(gl)) * Math.exp(-agl / 300);
    }
    // 風下の下降（弱い風ではほとんど効かず、強い風ほど強い）
    const lee = this.leeAt(x, y, z);
    if (lee > 0) lift -= lee * 1.2 * Math.min(Math.max((windMs * 3.6 - 10) / 20, 0), 1.5);
    // 背景の下降（サーマルの外側）
    const ratio = this.thermalRatio(timeOfDayS);
    lift -= 0.15 * ratio;
    // サーマル
    lift += this.thermalLift(x, y, z, timeOfDayS, w);
    // 雷雲の真下の吸い込み
    for (const s of this.state.storms) {
      if (s.phase !== 'mature') continue;
      const d = Math.hypot(x - s.x, z - s.z);
      if (d < s.radiusM) lift += 2.5 * (1 - d / s.radiusM);
    }
    return lift;
  }

  private thermalTop(t: Thermal, timeOfDayS: number): number {
    const cb = this.cloudBaseAt(timeOfDayS);
    return t.hasCloud ? cb : t.groundM + 0.8 * (cb - t.groundM);
  }

  private thermalCore(t: Thermal, timeOfDayS: number): number {
    const age = timeOfDayS - t.bornS;
    const env = smoothstep(0, 120, age) * (1 - smoothstep(t.lifeS - 180, t.lifeS, age));
    return t.strengthMs * env;
  }

  /**
   * 高度 y でのサーマルの中心。サーマルは柱ごと風に流され（updateThermals）、柱の傾きは小さめ
   * （「風速 ÷ (上昇率 × 4)」程度）とする。
   */
  thermalCenterAt(t: Thermal, y: number, wind: Vec2): Vec2 {
    const rise = Math.max(0, y - t.groundM) / Math.max(t.strengthMs * 4, 6);
    return { x: t.x + wind.x * rise, z: t.z + wind.z * rise };
  }

  private thermalLift(x: number, y: number, z: number, timeOfDayS: number, wind: Vec2): number {
    if (this.state.thermals.length === 0) return 0;
    // 傾きを見込んで、根元の位置で探す
    const ground = this.terrain.heightAt(x, z);
    const back = Math.max(0, y - ground) / 10;
    const qx = x - wind.x * back;
    const qz = z - wind.z * back;
    const ci = Math.floor(qx / HASH_M);
    const cj = Math.floor(qz / HASH_M);
    let lift = 0;
    for (let dj = -2; dj <= 2; dj++) {
      for (let di = -2; di <= 2; di++) {
        const list = this.hash.get((cj + dj) * 1000 + (ci + di));
        if (!list) continue;
        for (const t of list) {
          const top = this.thermalTop(t, timeOfDayS);
          if (y > top) continue;
          const c = this.thermalCenterAt(t, y, wind);
          const r = Math.hypot(x - c.x, z - c.z);
          const R = t.radiusM;
          if (r > R * 2.6) continue;
          const W = this.thermalCore(t, timeOfDayS);
          const core =
            W * Math.exp(-((r / R) * (r / R))) - 0.3 * W * Math.exp(-(((r - 1.6 * R) / (0.5 * R)) ** 2));
          const low = Math.min(Math.max((y - t.groundM) / 50, 0), 1);
          const high = 1 - smoothstep(top - 200, top, y);
          lift += core * low * high;
        }
      }
    }
    return lift;
  }

  /** 地形の高さ（見えている情報。CPU の見積もり用） */
  terrainHeight(x: number, z: number): number {
    return this.terrain.heightAt(x, z);
  }

  /** 雲の下のサーマル（雲がまだ残っていても、サーマルが終わっていれば undefined） */
  thermalById(id: number): Thermal | undefined {
    return this.byId.get(id);
  }

  /** いちばん近いサーマルの中心までの距離と半径（アシストのセンタリング・突風の判定用） */
  nearestThermal(
    x: number,
    y: number,
    z: number,
    timeOfDayS: number,
  ): { dx: number; dz: number; r: number; R: number } | null {
    const w = this.windAt(x, y, z, timeOfDayS, { x: 0, z: 0 });
    let best: { dx: number; dz: number; r: number; R: number } | null = null;
    for (const t of this.state.thermals) {
      if (y > this.thermalTop(t, timeOfDayS)) continue;
      const c = this.thermalCenterAt(t, y, w);
      const r = Math.hypot(c.x - x, c.z - z);
      if (r < t.radiusM * 2.5 && (!best || r < best.r)) best = { dx: c.x - x, dz: c.z - z, r, R: t.radiusM };
    }
    return best;
  }

  /** 地上で雨が降っているか（成熟・消滅中の雷雲の下） */
  rainAt(x: number, z: number): boolean {
    return this.state.storms.some(
      (s) => (s.phase === 'mature' || s.phase === 'dissipating') && Math.hypot(x - s.x, z - s.z) < s.radiusM,
    );
  }

  /** 最も近い成熟した雷雲までの距離（雷雲の縁から。中なら負） */
  stormDistance(x: number, z: number): number {
    let best = Infinity;
    for (const s of this.state.storms) {
      if (s.phase !== 'mature' && s.phase !== 'dissipating') continue;
      best = Math.min(best, Math.hypot(x - s.x, z - s.z) - s.radiusM);
    }
    return best;
  }

  // ---- 時間を進める ----

  step(timeOfDayS: number, dtS: number, rng: Rng, emit: (e: WeatherEvent) => void): void {
    this.updateLee(timeOfDayS);
    if (timeOfDayS - this.sourceTime >= 1800) this.updateSources(timeOfDayS);
    this.updateThermals(timeOfDayS, dtS, rng);
    this.updateStorms(timeOfDayS, dtS, emit);
    this.updateClouds(timeOfDayS);
  }

  private updateLee(timeOfDayS: number): void {
    const w = baseWindAt(this.state.truth, timeOfDayS);
    const from = Math.atan2(-w.x, w.z);
    if (Number.isFinite(this.leeFromRad)) {
      const d = Math.abs(Math.atan2(Math.sin(from - this.leeFromRad), Math.cos(from - this.leeFromRad)));
      if (d < (15 * Math.PI) / 180) return;
    }
    this.leeFromRad = from;
    // 風上へ 3km たどる
    const ux = Math.sin(from);
    const uz = -Math.cos(from);
    for (let j = 0; j < this.gnz; j++) {
      for (let i = 0; i < this.gnx; i++) {
        const x = i * GRID_M;
        const z = j * GRID_M;
        const h0 = this.terrain.heightAt(x, z);
        let rise = 0;
        for (let d = 250; d <= 3000; d += 250)
          rise = Math.max(rise, this.terrain.heightAt(x + ux * d, z + uz * d) - h0);
        const k = j * this.gnx + i;
        this.rise[k] = rise;
        this.lee[k] = Math.min(Math.max((rise - 400) / 800, 0), 1);
      }
    }
  }

  private updateSources(timeOfDayS: number): void {
    this.sourceTime = timeOfDayS;
    const sun = sunPosition(timeOfDayS + 900).dir;
    let acc = 0;
    let max = 0;
    for (let j = 0; j < this.gnz; j++) {
      for (let i = 0; i < this.gnx; i++) {
        const x = i * GRID_M;
        const z = j * GRID_M;
        const k = j * this.gnx + i;
        const b = this.terrain.biomeAt(x, z);
        let s = 0;
        if (b !== Biome.Lake && sun.y > 0) {
          const g = this.terrain.gradientAt(x, z);
          const inv = 1 / Math.hypot(g.gx, 1, g.gz);
          const incidence = Math.max(0, (-g.gx * sun.x + sun.y - g.gz * sun.z) * inv);
          const h = this.terrain.heightAt(x, z);
          s = incidence * (BIOME_THERMAL[b] as number) * (0.7 + 0.3 * smoothstep(800, 2500, h));
          // 端は使わない
          if (x < 1000 || z < 1000 || x > this.bounds.widthM - 1000 || z > this.bounds.depthM - 1000) s = 0;
        }
        this.source[k] = s;
        acc += s;
        this.sourceCdf[k] = acc;
        max = Math.max(max, s);
      }
    }
    this.sourceMax = max || 1;
  }

  private pickSourceCell(rng: Rng): number {
    const total = this.sourceCdf[this.sourceCdf.length - 1] as number;
    if (total <= 0) return -1;
    const u = rng.next() * total;
    let lo = 0;
    let hi = this.sourceCdf.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((this.sourceCdf[mid] as number) < u) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  private updateThermals(timeOfDayS: number, dtS: number, rng: Rng): void {
    const st = this.state;
    st.thermals = st.thermals.filter((t) => timeOfDayS - t.bornS < t.lifeS);
    // 柱ごとその高さの空気と一緒に流す（パイロットが飛ぶ 2000m 付近の風）
    const w = baseWindAt(st.truth, timeOfDayS);
    const drift = 0.95 * dtS;
    for (const t of st.thermals) {
      t.x += w.x * drift;
      t.z += w.z * drift;
    }
    const ratio = this.thermalRatio(timeOfDayS);
    if (ratio > 0) {
      const lambda = ((MAX_THERMALS * ratio) / MEAN_LIFE_S) * dtS;
      // ポアソン分布で発生数を決める
      let n = 0;
      const L = Math.exp(-lambda);
      for (let p = rng.next(); p > L && n < 20; p *= rng.next()) n++;
      const cb = this.cloudBaseAt(timeOfDayS);
      for (let s = 0; s < n && st.thermals.length < MAX_THERMALS; s++) {
        const k = this.pickSourceCell(rng);
        if (k < 0) break;
        const x = (k % this.gnx) * GRID_M + rng.range(-250, 250);
        const z = Math.floor(k / this.gnx) * GRID_M + rng.range(-250, 250);
        // maxClimbMs は「実際に上がれる速さ」。芯の上昇はそこにグライダーの旋回中の沈下（約 1.3m/s）を足したもの
        const net =
          st.truth.thermal.maxClimbMs *
          Math.sqrt(ratio) *
          (0.6 + 0.4 * ((this.source[k] as number) / this.sourceMax)) *
          rng.range(0.85, 1.15);
        const strength = net + THERMAL_SINK_OFFSET;
        if (net < 0.4) continue;
        const ground = this.terrain.heightAt(x, z);
        st.thermals.push({
          id: st.nextId++,
          x,
          z,
          groundM: ground,
          strengthMs: Math.min(strength, 6),
          radiusM: 80 + 170 * Math.min(Math.max((net - 0.5) / 3.5, 0), 1),
          bornS: timeOfDayS,
          lifeS: rng.range(1500, 3000),
          hasCloud: rng.chance(st.truth.cumulusRatio) && cb - ground > 300,
        });
      }
    }
    this.hash.clear();
    this.byId.clear();
    for (const t of st.thermals) {
      this.byId.set(t.id, t);
      const key = Math.floor(t.z / HASH_M) * 1000 + Math.floor(t.x / HASH_M);
      const list = this.hash.get(key);
      if (list) list.push(t);
      else this.hash.set(key, [t]);
    }
  }

  private updateStorms(timeOfDayS: number, dtS: number, emit: (e: WeatherEvent) => void): void {
    for (const s of this.state.storms) {
      const prev = s.phase;
      if (timeOfDayS >= s.endS + DISSIPATE_S) s.phase = 'done';
      else if (timeOfDayS >= s.endS) s.phase = 'dissipating';
      else if (timeOfDayS >= s.onsetS) s.phase = 'mature';
      else if (timeOfDayS >= s.precursorS) s.phase = 'precursor';
      if (prev !== s.phase) {
        if (s.phase === 'precursor') emit({ type: 'cuDeveloping', x: s.x, z: s.z });
        if (s.phase === 'mature') emit({ type: 'stormFormed', x: s.x, z: s.z });
        if (s.phase === 'dissipating') emit({ type: 'stormDissipated', x: s.x, z: s.z });
      }
      if (s.phase === 'mature' || s.phase === 'dissipating') {
        // 上空 3000m の風で流される
        const w = baseWindAt(this.state.truth, timeOfDayS);
        const f = altitudeWindFactor(3000) * 0.6;
        s.x += w.x * f * dtS;
        s.z += w.z * f * dtS;
      }
      s.radiusM =
        s.phase === 'mature'
          ? s.maxRadiusM * smoothstep(s.onsetS, s.onsetS + 900, timeOfDayS)
          : s.phase === 'dissipating'
            ? s.maxRadiusM * (1 - smoothstep(s.endS, s.endS + DISSIPATE_S, timeOfDayS))
            : 0;
    }
  }

  private updateClouds(timeOfDayS: number): void {
    const st = this.state;
    const cb = this.cloudBaseAt(timeOfDayS);
    const byThermal = new Map(st.clouds.map((c) => [c.thermalId, c]));
    for (const t of st.thermals) {
      if (!t.hasCloud || timeOfDayS - t.bornS < 180 || cb - t.groundM < 300) continue;
      const w = this.windAt(t.x, cb, t.z, timeOfDayS, { x: 0, z: 0 });
      const c = this.thermalCenterAt(t, cb, w);
      const size = 350 + 220 * t.strengthMs;
      const existing = byThermal.get(t.id);
      if (existing) {
        existing.x = c.x;
        existing.z = c.z;
        existing.baseM = cb;
        existing.dieS = t.bornS + t.lifeS + 300;
      } else {
        st.clouds.push({
          id: st.nextId++,
          thermalId: t.id,
          x: c.x,
          z: c.z,
          baseM: cb,
          sizeM: size,
          topM: cb + size * 0.8,
          bornS: timeOfDayS,
          dieS: t.bornS + t.lifeS + 300,
          towering: 0,
        });
      }
    }
    st.clouds = st.clouds.filter((c) => timeOfDayS < c.dieS);
    // 雷雨の前触れ：発生予定地点の周りの積雲が高く育つ
    for (const c of st.clouds) {
      let tower = 0;
      for (const s of st.storms) {
        if (s.phase === 'waiting' || s.phase === 'done') continue;
        if (Math.hypot(c.x - s.x, c.z - s.z) > 6000) continue;
        tower = Math.max(tower, smoothstep(s.precursorS, s.onsetS, timeOfDayS));
      }
      c.towering = tower;
      c.topM = c.baseM + c.sizeM * 0.8 + tower * Math.max(0, 7000 - c.baseM);
    }
  }

  /** 突風の発生率（1 分あたり。docs/03_simulation.md 8 章） */
  gustRatePerMin(
    x: number,
    y: number,
    z: number,
    timeOfDayS: number,
    refWindKmh: number,
    base: number,
  ): number {
    const w = this.windAt(x, y, z, timeOfDayS, { x: 0, z: 0 });
    const kmh = Math.hypot(w.x, w.z) * 3.6;
    let rate = base * (kmh / refWindKmh) ** 2;
    if (this.leeAt(x, y, z) > 0.3) rate *= 3;
    const th = this.nearestThermal(x, y, z, timeOfDayS);
    if (th && th.r > th.R * 0.8 && th.r < th.R * 1.8) rate *= 1.5;
    if (this.stormDistance(x, z) < 10_000) rate *= 4;
    return rate;
  }
}
